import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import type { RFBOptionLetter } from '@/config/instruments';
import { RFB_SJ_8_ITEMS } from '@/config/instruments';

export interface RFBSJRunnerProps {
  commercialName: string;
  onComplete: (firstChoices: RFBOptionLetter[], avoidChoices: RFBOptionLetter[]) => void;
  initialFirst?: RFBOptionLetter[];
  initialAvoid?: RFBOptionLetter[];
}

const OPTIONS: RFBOptionLetter[] = ['A', 'B', 'C', 'D'];

export function RFBSJRunner({
  commercialName,
  onComplete,
  initialFirst = [],
  initialAvoid = [],
}: RFBSJRunnerProps) {
  const pad8 = (arr: RFBOptionLetter[]) =>
    [...arr, ...(Array(8 - arr.length).fill(undefined) as (RFBOptionLetter | undefined)[])].slice(0, 8);
  const [firstChoices, setFirstChoices] = useState<(RFBOptionLetter | undefined)[]>(() => pad8(initialFirst));
  const [avoidChoices, setAvoidChoices] = useState<(RFBOptionLetter | undefined)[]>(() => pad8(initialAvoid));
  const [currentIndex, setCurrentIndex] = useState(0);
  const [errorSameOption, setErrorSameOption] = useState(false);

  const setFirst = useCallback((idx: number, letter: RFBOptionLetter) => {
    setFirstChoices((prev) => {
      const next = [...prev];
      next[idx] = letter;
      return next;
    });
    setErrorSameOption(false);
  }, []);

  const setAvoid = useCallback((idx: number, letter: RFBOptionLetter) => {
    setAvoidChoices((prev) => {
      const next = [...prev];
      next[idx] = letter;
      return next;
    });
    setErrorSameOption(false);
  }, []);

  const item = RFB_SJ_8_ITEMS[currentIndex];
  const first = firstChoices[currentIndex];
  const avoid = avoidChoices[currentIndex];
  const sameOption = first && avoid && first === avoid;

  const allComplete = RFB_SJ_8_ITEMS.every((_, i) => {
    const f = firstChoices[i];
    const a = avoidChoices[i];
    return f && a && f !== a;
  });

  const handleNext = () => {
    if (sameOption) {
      setErrorSameOption(true);
      return;
    }
    setErrorSameOption(false);
    if (currentIndex < RFB_SJ_8_ITEMS.length - 1) {
      setCurrentIndex((i) => i + 1);
    } else if (allComplete) {
      onComplete(
        firstChoices.filter((x): x is RFBOptionLetter => x != null) as RFBOptionLetter[],
        avoidChoices.filter((x): x is RFBOptionLetter => x != null) as RFBOptionLetter[]
      );
    }
  };

  const handleSubmit = () => {
    if (!allComplete || sameOption) {
      if (sameOption) setErrorSameOption(true);
      return;
    }
    setErrorSameOption(false);
    onComplete(
      firstChoices.filter((x): x is RFBOptionLetter => x != null) as RFBOptionLetter[],
      avoidChoices.filter((x): x is RFBOptionLetter => x != null) as RFBOptionLetter[]
    );
  };

  if (!item) return null;

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="text-lg">{commercialName}</CardTitle>
        <p className="text-sm text-muted-foreground">
          Situación {currentIndex + 1} de {RFB_SJ_8_ITEMS.length}
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="font-medium">{item.stem}</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <label className="text-sm font-medium">¿Qué harías primero?</label>
            <select
              value={first ?? ''}
              onChange={(e) => setFirst(currentIndex, (e.target.value as RFBOptionLetter) || 'A')}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">Seleccione...</option>
              {OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}) {item.options.find((o) => o.letter === opt)?.text}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">¿Qué evitarías?</label>
            <select
              value={avoid ?? ''}
              onChange={(e) => setAvoid(currentIndex, (e.target.value as RFBOptionLetter) || 'A')}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">Seleccione...</option>
              {OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}) {item.options.find((o) => o.letter === opt)?.text}
                </option>
              ))}
            </select>
          </div>
        </div>
        {errorSameOption && (
          <p className="text-sm text-destructive">
            Debe elegir opciones distintas para &quot;Qué harías primero&quot; y &quot;Qué evitarías&quot;.
          </p>
        )}
        <div className="flex justify-between">
          <Button
            type="button"
            variant="outline"
            disabled={currentIndex === 0}
            onClick={() => setCurrentIndex((i) => i - 1)}
          >
            Anterior
          </Button>
          {currentIndex < RFB_SJ_8_ITEMS.length - 1 ? (
            <Button
              type="button"
              onClick={handleNext}
              disabled={!first || !avoid}
            >
              Siguiente
            </Button>
          ) : (
            <Button type="button" onClick={handleSubmit} disabled={!allComplete}>
              Finalizar prueba
            </Button>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          Completadas: {RFB_SJ_8_ITEMS.filter((_, i) => firstChoices[i] && avoidChoices[i] && firstChoices[i] !== avoidChoices[i]).length} / {RFB_SJ_8_ITEMS.length}
        </p>
      </CardContent>
    </Card>
  );
}
